import { useState } from 'react';
import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { Icon } from '@/components/common/Icon';
import { Image } from '@/components/common/image';
import {
  PlayButton,
  Shadow,
  VideoDetails,
} from '@/components/common/WistiaComponents/Shared';
import { WistiaEmbed } from '@/components/common/WistiaEmbed';

import type { StandardImage, WistiaPopover } from '@/types/shared';

interface WistiaInlinePlayerProps {
  image: StandardImage;
  wistiaPopover: WistiaPopover;
}

export const WistiaInlinePlayer = ({
  image,
  wistiaPopover,
}: WistiaInlinePlayerProps) => {
  const [playPressed, setPlayPressed] = useState(false);

  return (
    <Container>
      <WistiaVideoContainer hasBorderRadius={!playPressed}>
        {!playPressed ? (
          <>
            <Shadow />
            <PlayButtonContainer
              className={
                wistiaPopover.trackingTag?.blockName &&
                `tracking-${wistiaPopover.trackingTag.blockName}__${wistiaPopover.trackingTag.blockAction}`
              }
              onClick={() => setPlayPressed(true)}
              aria-hidden="true"
            >
              <PlayButton>
                <Icon icon="Play" size="24px" aria-hidden="true" />
              </PlayButton>
            </PlayButtonContainer>

            <ImageContainer>
              <ThumbnailImage {...image} objectFit="cover" />
            </ImageContainer>
            <VideoDetails wistiaPopover={wistiaPopover} />
          </>
        ) : (
          <WistiaEmbed videoId={wistiaPopover.videoId} inline />
        )}
      </WistiaVideoContainer>
    </Container>
  );
};

const Container = styled.div`
  position: relative;
`;

const ThumbnailImage = styled(Image)`
  height: 100%;
  display: block;
`;

const ImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: 320px;

  > img {
    max-height: 320px;
  }

  ${screen.lg} {
    max-height: 340px;

    > img {
      max-height: 340px;
    }
  }
`;

const WistiaVideoContainer = styled.div<{ hasBorderRadius: boolean }>`
  position: relative;
  overflow: hidden;
  border-radius: ${({ hasBorderRadius }) =>
    hasBorderRadius ? 'var(--radius-component-xl)' : '0'};
`;

const PlayButtonContainer = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 3;
  top: 0;
  border: none;
  background-color: transparent;
  cursor: pointer;
  width: 100%;
  height: 100%;

  svg {
    pointer-events: none;

    path {
      stroke: transparent;
      fill: var(--surface-warm-base);
    }
  }
`;
